import { Grid, Typography } from '@mui/material';
import Button from 'components/Button';
import { AppTextInput } from 'components/FormElements';
import FullHeightView from 'components/new/FullHeightView';
import BasicSelect from 'further-ui/components/BasicSelect';
import DatePicker from 'further-ui/components/DatePicker';
import FormField from 'further-ui/components/FormField';
import { Column, Row } from 'further-ui/components/Layout';
import useEqualisations from 'hooks/lp/data/equalisations/useEqualisations';
import api from 'lib/trpcClient';
import { Controller, useFormContext } from 'react-hook-form';
import { CapitalActionFormValues } from './hooks/useCapitalActionForm';

type Props = {
  onNext: () => void;
  vehicleId: string;
};

const CapitalActionDetails: React.FC<Props> = ({ onNext, vehicleId }) => {
  const { control } = useFormContext<CapitalActionFormValues>();
  const [vehicles] = api.vehicle.listAllVehicles.useSuspenseQuery({
    order: 'desc',
    orderBy: 'vehicleName',
  });

  const { latestApprovedEqualisationDate } = useEqualisations(vehicleId);

  const vehicleOptions = vehicles.result.map((vehicle) => ({
    value: vehicle._id,
    label: vehicle.vehicleName,
  }));

  return (
    <FullHeightView
      actionBar={
        <Row justify="flex-end">
          <Button variant="contained" onClick={onNext} size="large">
            Save and next
          </Button>
        </Row>
      }
    >
      <Grid container>
        <Grid size={{ xs: 12, lg: 8 }}>
          <Column spacing="lg">
            <Typography variant="h4">Enter core capital action data</Typography>
            <FormField label="Vehicle" size="md">
              <BasicSelect
                fullWidth
                options={vehicleOptions}
                placeholder="Select vehicle"
                value={vehicleId}
                onChange={() => {}}
                disabled
              />
            </FormField>
            <FormField label="Capital action name" size="md">
              <Controller
                name="name"
                control={control}
                render={({ fieldState: { error }, field }) => (
                  <AppTextInput
                    {...field}
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </FormField>
            <FormField
              label="Capital action date"
              size="md"
              tooltip="This is the accounting date of this capital action."
            >
              <Controller
                name="ledgerDate"
                control={control}
                render={({
                  fieldState: { error },
                  field: { value, onChange, ...rest },
                }) => (
                  <DatePicker
                    fullWidth
                    error={!!error}
                    helperText={error?.message?.toString()}
                    value={value}
                    onChange={onChange}
                    minDate={latestApprovedEqualisationDate}
                    {...rest}
                  />
                )}
              />
            </FormField>

            <FormField
              label="Capital action due date"
              size="md"
              tooltip="This is the payment due date that will be communicated to LPs if the action is a net capital call."
            >
              <Controller
                name="actionDueDate"
                control={control}
                render={({
                  fieldState: { error },
                  field: { value, onChange, ...rest },
                }) => (
                  <DatePicker
                    fullWidth
                    error={!!error}
                    helperText={error?.message?.toString()}
                    value={value}
                    onChange={onChange}
                    minDate={latestApprovedEqualisationDate}
                    {...rest}
                  />
                )}
              />
            </FormField>
          </Column>
        </Grid>
      </Grid>
    </FullHeightView>
  );
};

export default CapitalActionDetails;
